// ─────────────────────────────────────────────
// pages/SchedulePage.js
// Pickup schedule page with rotating zone map (Leaflet)
//
// Dependencies:
//   - Leaflet 1.9.4 (BSD-2-Clause)
//   - CartoDB tiles (free tier)
//
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import React, { useRef, useEffect, useMemo } from "react";
import { NAVY, NAVY2, BLUE, BLUE2, STEEL, BORDER, card, BRAND_NAME, ZONES } from "../constants";
import PageShell from "../components/PageShell";

// ── Zone rotation config ────────────────────────────────────
const ROTATION_ANCHOR = new Date(2026, 4, 1); // May 1, 2026 (local time)
const ZONE_ORDER = [
  { key: "A", name: "Zone A", color: "#0487D9", bg: "#E8F4FD", textColor: "#1e40af", labelColor: "#1d4ed8" },
  { key: "B", name: "Zone B", color: "#03A66A", bg: "#E8F8F0", textColor: "#166534", labelColor: "#15803d" },
  { key: "C", name: "Zone C", color: "#6B4FBB", bg: "#F0ECF9", textColor: "#4c1d95", labelColor: "#6d28d9" },
  { key: "D", name: "Zone D", color: "#E07B2A", bg: "#FEF3E8", textColor: "#9a3412", labelColor: "#c2410c" },
];

// ── Map polygon coordinates per zone ────────────────────────
// Zones A and D have real surveyed coords. B and C are placeholders.
// TODO: Replace B and C with real polygon coords when surveyed.
const ZONE_COORDS = {
  A: [
    [41.12951,-112.00333],[41.12819,-112.00163],[41.12521,-112.00114],
    [41.12173,-112.00235],[41.11975,-112.00138],[41.11793,-111.99822],
    [41.11500,-111.99700],[41.11500,-111.97500],[41.11500,-111.96000],
    [41.11809,-111.95886],[41.11975,-111.96080],[41.12173,-111.96323],
    [41.12372,-111.96566],[41.12554,-111.96809],[41.12703,-111.97198],
    [41.12852,-111.97684],[41.12951,-111.98170],[41.13017,-111.98753],
    [41.13050,-111.99385],[41.13001,-111.99968],[41.12951,-112.00163],
    [41.12951,-112.00333],
  ],
  // Placeholder: Zone B sits between A and D, central area
  B: [
    [41.12521,-112.00114],[41.12173,-112.00235],[41.11975,-112.00138],
    [41.11793,-111.99822],[41.11500,-111.99700],[41.11500,-111.98500],
    [41.11700,-111.98200],[41.11900,-111.98400],[41.12100,-111.98800],
    [41.12300,-111.99200],[41.12521,-112.00114],
  ],
  // Placeholder: Zone C sits southwest of the housing area
  C: [
    [41.11500,-111.99700],[41.11500,-111.98500],[41.11700,-111.98200],
    [41.11412,-111.99725],[41.11147,-111.99482],[41.10983,-111.99288],
    [41.10783,-111.99045],[41.10518,-111.98850],[41.10700,-111.98000],
    [41.11000,-111.97800],[41.11300,-111.98000],[41.11500,-111.98500],
  ],
  D: [
    [41.11500,-111.99700],[41.11412,-111.99725],[41.11147,-111.99482],
    [41.10983,-111.99288],[41.10783,-111.99045],[41.10518,-111.98850],
    [41.10253,-111.98777],[41.10088,-111.98559],[41.09989,-111.98267],
    [41.09939,-111.97976],[41.09922,-111.97684],[41.09939,-111.97295],
    [41.09989,-111.96906],[41.10055,-111.96518],[41.10138,-111.96129],
    [41.10220,-111.95691],[41.10320,-111.95351],[41.10452,-111.95157],
    [41.10651,-111.95060],[41.10849,-111.95108],[41.11048,-111.95254],
    [41.11246,-111.95448],[41.11445,-111.95546],[41.11644,-111.95691],
    [41.11500,-111.96000],[41.11500,-111.97500],[41.11500,-111.99700],
  ],
};

// Map label center points per zone
const ZONE_LABEL_POS = {
  A: [41.1245, -111.9880],
  B: [41.1190, -111.9920],
  C: [41.1100, -111.9880],
  D: [41.1080, -111.9760],
};

function getActiveZoneIndex() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffMs = now.getTime() - ROTATION_ANCHOR.getTime();
  if (diffMs < 0) return 0; // before launch, show Zone A
  const diffDays = Math.floor(diffMs / 86400000);
  return Math.floor(diffDays / 7) % 4;
}

function getWeekDateRange(weekOffset) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffMs = now.getTime() - ROTATION_ANCHOR.getTime();
  const currentWeek = diffMs < 0 ? 0 : Math.floor(Math.floor(diffMs / 86400000) / 7);
  const targetWeek = currentWeek + weekOffset;
  const start = new Date(ROTATION_ANCHOR);
  start.setDate(start.getDate() + targetWeek * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(start)} to ${fmt(end)}`;
}

export default function SchedulePage({ nav }) {
  const schedMapRef  = useRef(null);
  const schedMapInst = useRef(null);

  const activeIdx = useMemo(() => getActiveZoneIndex(), []);
  const active = ZONE_ORDER[activeIdx];
  const coords = ZONE_COORDS[active.key];
  const zoneData = ZONES.find(z => z.key === active.key);
  const streets = zoneData?.streets?.split(", ") || [];

  // Next 3 zones in rotation
  const upcoming = [1, 2, 3].map(offset => {
    const idx = (activeIdx + offset) % 4;
    return { ...ZONE_ORDER[idx], week: getWeekDateRange(offset) };
  });

  useEffect(() => {
    function initScheduleMap() {
      if (!schedMapRef.current || !window.L) return;
      if (schedMapInst.current) { schedMapInst.current.remove(); schedMapInst.current = null; }
      const map = window.L.map(schedMapRef.current, { zoomControl: false, scrollWheelZoom: false, dragging: true });
      window.L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", { maxZoom: 20, subdomains: "abcd" }).addTo(map);

      // Show all zones dimmed, active zone highlighted
      ZONE_ORDER.forEach(z => {
        const isActive = z.key === active.key;
        window.L.polygon(ZONE_COORDS[z.key], {
          color: z.color, weight: isActive ? 3 : 1.5,
          fillColor: z.color, fillOpacity: isActive ? 0.3 : 0.08,
        }).addTo(map).bindPopup(`<b style='color:${z.color}'>${z.name}</b>`);
      });

      // Label for active zone
      const pos = ZONE_LABEL_POS[active.key];
      window.L.marker(pos, { icon: window.L.divIcon({ className: "", html: `<div style='background:${active.color};color:#fff;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:700;white-space:nowrap'>${active.name} — Active</div>`, iconAnchor: [50, 10] }) }).addTo(map);

      const allCoords = Object.values(ZONE_COORDS).flat();
      map.fitBounds(window.L.latLngBounds(allCoords), { padding: [20, 20] });
      setTimeout(() => map.invalidateSize(), 100);
      schedMapInst.current = map;
    }

    if (!document.getElementById("leaflet-css")) {
      const lc = document.createElement("link");
      lc.id = "leaflet-css"; lc.rel = "stylesheet";
      lc.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(lc);
    }
    if (window.L) {
      initScheduleMap();
    } else {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      s.onload = initScheduleMap;
      document.head.appendChild(s);
    }
    return () => { try { schedMapInst.current?.remove(); schedMapInst.current = null; } catch (e) {} };
  }, [active]);

  return (
    <PageShell>
      <div style={{ background: `linear-gradient(135deg,${NAVY},${NAVY2})`, padding: "28px 20px 24px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#0487D9", marginBottom: 4 }}>Hill AFB Housing</div>
        <h2 style={{ fontWeight: 900, fontSize: 24, color: "#fff", margin: "0 0 4px" }}>Pickup Schedule</h2>
        <div style={{ fontSize: 13, color: STEEL, marginTop: 6 }}>Zones rotate weekly. Your zone is detected from your address.</div>
      </div>

      <div style={{ padding: "20px 18px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Zone map */}
        <div ref={schedMapRef} style={{ height: 260, borderRadius: 14, overflow: "hidden", border: `1px solid ${BORDER}`, position: "relative", zIndex: 0 }} />

        {/* Active zone card */}
        <div style={{ borderRadius: 16, border: `2px solid ${active.color}`, overflow: "hidden", boxShadow: `0 2px 12px ${active.color}22` }}>
          <div style={{ background: active.color, padding: "14px 20px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 900, fontSize: 18, color: "#fff" }}>{active.name}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.8)", background: "rgba(255,255,255,.15)", padding: "3px 10px", borderRadius: 20 }}>Active Now</div>
          </div>
          <div style={{ background: active.bg, padding: "14px 20px" }}>
            <div style={{ display: "flex", marginBottom: 14 }}>
              <div style={{ flex: 1, borderRight: `1px solid ${active.color}22`, paddingRight: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: active.labelColor, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Tuesday</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: active.textColor }}>10:00 AM to 1:00 PM</div>
              </div>
              <div style={{ flex: 1, paddingLeft: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: active.labelColor, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Saturday</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: active.textColor }}>12:00 PM to 3:00 PM</div>
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: active.labelColor, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Streets in this zone</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {streets.map((s, i) => (
                <div key={i} style={{ background: "#fff", border: `1px solid ${active.color}22`, borderRadius: 20, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: active.textColor }}>{s.trim()}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Coming up next */}
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 12 }}>Coming up next</div>
          {upcoming.map((z, i) => (
            <div key={z.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: z.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>{z.name}</div>
                <div style={{ fontSize: 12, color: STEEL }}>{z.week}</div>
              </div>
            </div>
          ))}
        </div>

        {/* How zones work */}
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 10 }}>How zones work</div>
          {[
            "Your zone is assigned automatically from your address when you book.",
            "Each zone gets one week in a four week rotation. Pickups run Tuesday and Saturday.",
            "The loader arrives during the pickup window. No need to be home.",
            "Have items at your driveway or outdoor placement area before the window starts.",
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < 3 ? 8 : 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#0487D9", flexShrink: 0, marginTop: 6 }} />
              <div style={{ fontSize: 13, color: STEEL, lineHeight: 1.5 }}>{t}</div>
            </div>
          ))}
        </div>

        <button onClick={() => nav("schedule")} style={{ width: "100%", padding: "15px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${BLUE},${BLUE2})`, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 16px rgba(21,101,192,.3)" }}>
          Book a Pickup
        </button>

        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: NAVY, letterSpacing: 0.5 }}>{BRAND_NAME.toUpperCase()} SERVICES</div>
        </div>
      </div>
    </PageShell>
  );
}
